import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps,
} from '../studentAdmission.interfaces';
import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import ControlledTextField from '../../../../../../components/controlledComponents/ControlledTextField.component';
import { TStatus } from '../../../../../../../apis/finance/finance.interfaces';
import {
  IFeeComponentDefinition,
  IFeeCategory,
} from 'src/apis/institute/institute.interfaces';
import { IFeeComponentDto } from 'src/apis/students/students.interfaces';
import { useFeeStructureHook } from '../../../../../institute/hooks/useFeeStructureHook';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { zNumberFromInput } from 'src/utils/zodFormHelpers';
import isAppWithMock from 'src/utils/isAppWithMock';

type FeeComponentWithCategory = IFeeComponentDefinition & {
  categoryId: string;
  categoryName: string;
};

const feeSelectionSchema = z.record(z.boolean()).refine(
  (val) => typeof val === 'object',
  { message: 'Invalid selection' }
);

const feeOptionSchema = z.object({
  feeId: z.string(),
  paidAmount: zNumberFromInput({ min: 0 }),
  pendingAmount: zNumberFromInput({ min: 0 }),
});

const AdmissionStep4FeesSchema = z.object({
  feeSelections: feeSelectionSchema.optional(),
  feeOptions: z.array(feeOptionSchema).optional(),
});

type Step4FormData = z.infer<typeof AdmissionStep4FeesSchema>;

export type Step4FeePersistPayload = {
  feeComponents: Omit<IFeeComponentDto, 'createdBy'>[];
};

type FeeRowData = FeeComponentWithCategory & {
  paidAmount: number;
  status: TStatus;
  pendingAmount: number;
};

const flattenFeeCategories = (
  categories: IFeeCategory[]
): FeeComponentWithCategory[] => {
  return categories.flatMap((cat) =>
    cat.components.map((c) => ({
      ...c,
      categoryId: cat.categoryId,
      categoryName: cat.categoryName,
    }))
  );
};

const defaultValues: Step4FormData = {
  feeSelections: {},
  feeOptions: [],
};

const AdmissionStep4FeesView = forwardRef<
  AdmissionStepSubmitHandle,
  StudentAdmissionStepProps<Step4FeePersistPayload>
>(function AdmissionStep4FeesView(
  {
    onCompleteHandler,
    studentDetails,
    step3Data,
    onStepValidityChange,
    onPersistStep,
  },
  ref
) {
  const isMockData = isAppWithMock();
  const { translateText } = useTranslation();

  const params = useMemo(() => {
    const grade = studentDetails?.grade ?? step3Data?.grade ?? '';
    const section = studentDetails?.section ?? step3Data?.section ?? '';
    const category = studentDetails?.category ?? step3Data?.category ?? '';
    if (!grade || !category) return null;
    return { grade, section, category };
  }, [studentDetails, step3Data]);

  const { data: feeStructure, isLoading } = useFeeStructureHook(params, {
    enabled: !!params,
  });

  const flatComponents = useMemo(
    () => (feeStructure ? flattenFeeCategories(feeStructure) : []),
    [feeStructure]
  );

  const feeRowsWithStatus = useMemo<FeeRowData[]>(() => {
    if (flatComponents.length === 0) return [];
    const existing = studentDetails?.feeDetails?.feeComponents || [];
    return flatComponents.map((comp) => {
      const matched = existing.find((c) => c.componentId === comp.componentId);
      const paidAmount = matched?.paidAmount ?? 0;
      const pendingAmount = Math.max(0, comp.amount - paidAmount);
      let status: TStatus = TStatus.UNPAID;
      if (paidAmount >= comp.amount) status = TStatus.PAID;
      else if (paidAmount > 0) status = TStatus.PARTIAL;
      return {
        ...comp,
        paidAmount,
        status,
        pendingAmount,
      };
    });
  }, [flatComponents, studentDetails]);

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
    watch,
    reset,
    setValue,
  } = useForm<Step4FormData>({
    resolver: zodResolver(AdmissionStep4FeesSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const watchedFeeSelections = watch('feeSelections');
  const feeSelections = useMemo(() => watchedFeeSelections ?? {}, [watchedFeeSelections]);

  useEffect(() => {
    const initialSelections: Record<string, boolean> = {};
    feeRowsWithStatus.forEach((row) => {
      const optedOut = studentDetails?.feeDetails?.feeComponents
        ? !studentDetails.feeDetails.feeComponents.some(
          (c) => c.componentId === row.componentId
        ) && row.isOptional
        : false;
      initialSelections[row.componentId] = !optedOut;
    });
    reset({
      feeSelections: initialSelections,
      feeOptions: feeRowsWithStatus.map((f) => ({
        feeId: f.componentId,
        paidAmount: f.paidAmount,
        pendingAmount: f.pendingAmount,
      })),
    });
  }, [feeRowsWithStatus, studentDetails, reset]);

  useEffect(() => {
    if (studentDetails && Object.keys(errors).length === 0) {
      const timeoutId = setTimeout(() => onCompleteHandler(), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [studentDetails, errors, onCompleteHandler]);

  const buildFeeSubmissionPayload = useCallback((): Step4FeePersistPayload | null => {
    const mandatoryViolation = feeRowsWithStatus.some(
      (r) => !r.isOptional && feeSelections[r.componentId] === false
    );
    if (mandatoryViolation) return null;
    const selected = feeRowsWithStatus.filter(
      (r) => feeSelections[r.componentId] !== false
    );
    // Mirror BE: feeComponents must be non-empty ("Fee details are required.").
    if (selected.length === 0) return null;
    const feeComponents = selected.map((row) => ({
      totalFee: row.amount,
      status: row.status,
      amount: row.amount,
      paidAmount: row.paidAmount,
      componentName: row.componentName,
      dueDate: new Date().toISOString(),
      academicYear: new Date().getFullYear(),
      purpose: row.categoryName || 'tuition',
    }));
    return { feeComponents };
  }, [feeRowsWithStatus, feeSelections]);

  const hasSelectedComponents = useMemo(
    () =>
      feeRowsWithStatus.some((r) => feeSelections[r.componentId] !== false),
    [feeRowsWithStatus, feeSelections]
  );

  useEffect(() => {
    // Mirror BE non-empty feeComponents rule: a step with nothing selected can't be submitted.
    onStepValidityChange?.(isValid && hasSelectedComponents);
  }, [isValid, hasSelectedComponents, onStepValidityChange]);

  useImperativeHandle(ref, () => ({
    submitStep: async () => {
      const payload = buildFeeSubmissionPayload();
      if (!payload) {
        return false;
      }
      try {
        if (onPersistStep) {
          await onPersistStep(payload);
        }
        onCompleteHandler(payload);
        return true;
      } catch {
        return false;
      }
    },
  }));

  const selectedRows = useMemo(
    () =>
      feeRowsWithStatus.filter(
        (r) => feeSelections[r.componentId] !== false
      ),
    [feeRowsWithStatus, feeSelections]
  );

  const totalAmount = selectedRows.reduce((acc, r) => acc + r.amount, 0);
  const totalPaid = selectedRows.reduce((acc, r) => acc + r.paidAmount, 0);
  const totalPending = selectedRows.reduce((acc, r) => acc + r.pendingAmount, 0);

  const onSubmit = () => {
    const payload = buildFeeSubmissionPayload();
    if (!payload) return;
    onCompleteHandler(payload);
  };

  if (!params) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        padding={{ xs: 1, sm: 2 }}
        data-testid="admission-step-4-no-fees-container"
      >
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: { xs: 0.5, sm: 1 } }}>
            {translateText('students.admission.noFeeOptionsFound')}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {translateText('students.admission.addGradeAndCategoryMessage')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container justifyContent="center" padding={4} data-testid="admission-step-4-loading">
        <CircularProgress />
      </Grid>
    );
  }

  if (!feeStructure || feeRowsWithStatus.length === 0) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        padding={{ xs: 1, sm: 2 }}
        data-testid="admission-step-4-no-fees-container"
      >
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: { xs: 0.5, sm: 1 } }}>
            {translateText('students.admission.noFeeOptionsFound')}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {translateText('students.admission.addGradeAndCategoryMessage')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const getStatusColor = (status: TStatus) => {
    switch (status) {
      case TStatus.PAID: return 'success';
      case TStatus.PARTIAL: return 'warning';
      case TStatus.UNPAID: return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: TStatus) => {
    switch (status) {
      case TStatus.PAID: return translateText('students.fees.paid');
      case TStatus.PARTIAL: return translateText('students.fees.partial');
      case TStatus.UNPAID: return translateText('students.fees.unpaid');
      default: return 'N/A';
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: '100%' }}
      id="admission-step-4-form"
      data-testid="admission-step-4-form"
    >
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        padding={{ xs: 1, sm: 2 }}
        data-testid="admission-step-4-form-container"
      >
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: { xs: 1, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {translateText('students.admission.feeComponentsBasedOnGrade')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid
                container
                spacing={{ xs: 1, sm: 2 }}
                sx={{
                  py: { xs: 1, sm: 1.5 },
                  borderBottom: 1,
                  borderColor: 'divider',
                  backgroundColor: 'action.hover',
                }}
                data-testid="fee-table-header"
              >
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('students.admission.feeOptIn')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('students.fees.feeType')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('common.status')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('students.fees.expectedAmount')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('students.fees.paidAmount')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {translateText('students.fees.pendingAmount')}
                  </Typography>
                </Grid>
              </Grid>

              {feeRowsWithStatus.map((row, index) => {
                const isOptedIn = feeSelections[row.componentId] !== false;
                const tooltipContent = (
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <strong>{translateText('students.fees.feeId')}:</strong> {row.componentId}
                    </Typography>
                    {row.description && (
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        <strong>{translateText('common.description')}:</strong> {row.description}
                      </Typography>
                    )}
                    {row.isOptional && (
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                        {translateText('students.admission.optionalComponent')}
                      </Typography>
                    )}
                  </Box>
                );

                return (
                  <Grid
                    container
                    spacing={{ xs: 1, sm: 2 }}
                    alignItems="center"
                    key={row.componentId}
                    sx={{
                      py: { xs: 1, sm: 2 },
                      borderBottom: index < feeRowsWithStatus.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      opacity: isOptedIn ? 1 : 0.6,
                    }}
                    data-testid={`fee-row-${index}`}
                  >
                    <Grid item xs={12} md={2}>
                      {row.isOptional ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isOptedIn}
                              onChange={(_, checked) =>
                                setValue(`feeSelections.${row.componentId}`, checked, { shouldValidate: true })
                              }
                              data-testid={`fee-opt-in-${row.componentId}`}
                            />
                          }
                          label={translateText('students.admission.optIn')}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {translateText('students.admission.mandatory')}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Tooltip title={tooltipContent} arrow>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {row.componentName}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Chip
                        label={getStatusLabel(row.status)}
                        color={getStatusColor(row.status) as 'success' | 'warning' | 'error' | 'default'}
                        size="small"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        ₹{row.amount.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        ₹{row.paidAmount.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      {row.pendingAmount > 0 && isOptedIn ? (
                        <ControlledTextField
                          id={`pending-amount-${row.componentId}`}
                          label=""
                          name={`feeOptions.${index}.pendingAmount`}
                          control={control}
                          type="number"
                          errorMessage={errors.feeOptions?.[index]?.pendingAmount?.message}
                          isGridItem={false}
                        />
                      ) : isOptedIn ? (
                        <Typography variant="body2" color="success.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {translateText('students.fees.fullyPaid')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Grid>
                  </Grid>
                );
              })}

              <Grid
                container
                spacing={{ xs: 1, sm: 2 }}
                sx={{
                  py: { xs: 1, sm: 2 },
                  mt: { xs: 0.5, sm: 1 },
                  borderTop: 2,
                  borderColor: 'primary.main',
                  backgroundColor: 'action.selected',
                }}
                data-testid="fee-table-footer"
              >
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {translateText('students.fees.total')} ({selectedRows.length} {translateText('students.admission.componentsSelected')})
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    ₹{totalAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    ₹{totalPaid.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    ₹{totalPending.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {!studentDetails ? (
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
            data-testid="admission-step-4-actions"
          >
            {isMockData ? (
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={() => onCompleteHandler()}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="admission-step-4-finish-button"
              >
                {translateText('students.admission.finishStep4')}
              </Button>
            ) : null}
          </Grid>
        ) : null}
      </Grid>
    </form>
  );
});

export default AdmissionStep4FeesView;
